import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Phone, 
  Mail, 
  Edit3, 
  Save, 
  X,
  LogOut,
  Sparkles,
  Star,
  Award,
  BookOpen,
  Check,
  Lock,
  ChevronRight
} from 'lucide-react';
import { auth, db, getCurrentUser, isUserAuthenticated } from '../Auth/firebase';
import { signOut } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import logo from '../logo.png';

import CommonLoader from '../CommonLoader';

export default function Profile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    const checkAuth = async () => {
      if (!isUserAuthenticated()) {
        navigate('/login');
        return;
      }

      try {
        const currentUser = getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
          const ref = doc(db, 'users', currentUser.uid);
          const docSnap = await getDoc(ref);
          if (docSnap.exists()) {
            const data = docSnap.data();
            setFormData({
              displayName: data.name || "",
              email: data.email || "",
              phone: currentUser.phoneNumber || "",
            });
          } else {
            setFormData({
              displayName: currentUser.displayName || '',
              email: currentUser.email || '',
              phone: currentUser.phoneNumber || '',
            });
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!user) return;
    try {
      const ref = doc(db, 'users', user.uid);
      await setDoc(ref, {
        name: formData.displayName,
        email: formData.email,
        mobile: formData.phone,
        updatedAt: new Date()
      }, { merge: true });
      setEditing(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error("Error saving profile:", err);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) return <CommonLoader />;

  if (!user) {
    return (
      <div className="min-h-screen bg-[#fafaf7] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 max-w-sm w-full border border-gold-300 shadow-2xl text-center space-y-4">
          <div className="w-14 h-14 bg-[#0b3a24] text-[#eddca6] rounded-2xl flex items-center justify-center mx-auto shadow-md">
            <User className="w-7 h-7" />
          </div>
          <h2 className="text-2xl font-serif font-bold text-[#0b3a24]">Access Denied</h2>
          <p className="text-xs text-[#0b3a24]/75 font-light">Please log in to view your investor profile.</p>
          <button
            onClick={() => navigate('/login')}
            className="gold-shine-btn w-full bg-gradient-to-r from-[#eddca6] via-[#fff3d1] to-[#eddca6] text-[#041a0f] font-sans font-extrabold text-xs uppercase tracking-widest py-3.5 rounded-xl shadow-xl hover:scale-105 transition-all"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#fafaf7] text-[#0b3a24] font-sans min-h-screen relative overflow-hidden">
      
      {/* Hero Header Banner (Dark Kerala Emerald Green & Gold Theme) */}
      <section className="relative bg-gradient-to-br from-[#041a0f] via-[#09100d] to-[#020906] text-white pt-36 pb-24 px-4 sm:px-6 lg:px-8 border-b border-[#eddca6]/20 overflow-hidden">
        
        {/* Soft Ambient Light Glow Blobs */}
        <div className="absolute top-1/3 left-1/4 -translate-x-1/2 w-[500px] h-[500px] bg-gradient-to-tr from-gold-900/20 to-transparent rounded-full filter blur-[140px] pointer-events-none"></div>
        <div className="absolute bottom-10 right-10 w-[300px] h-[300px] bg-[#eddca6]/10 rounded-full filter blur-[100px] pointer-events-none"></div>

        {/* Animated Blinking Stars */}
        <div className="absolute top-16 right-[15%] pointer-events-none animate-pulse">
          <Sparkles className="w-9 h-9 text-[#eddca6]/70 drop-shadow-[0_0_12px_rgba(237,220,166,0.5)]" />
        </div>
        <div className="absolute top-28 left-[6%] pointer-events-none animate-pulse">
          <Star className="w-8 h-8 text-[#eddca6]/50 fill-[#eddca6]/30 drop-shadow-[0_0_10px_rgba(237,220,166,0.4)]" />
        </div>

        <div className="max-w-5xl mx-auto text-center relative z-10 space-y-4">
          <div className="inline-flex items-center gap-2 bg-[#eddca6]/10 border border-[#eddca6]/30 text-[#eddca6] px-4.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-[#eddca6]" />
            CHIRAYIL GOLD CLUB MEMBER
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-black tracking-widest uppercase leading-tight text-white drop-shadow-md">
            INVESTOR <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#eddca6] via-[#f5edd2] to-[#c5a85a]">PROFILE</span>
          </h1>

          <p className="max-w-xl mx-auto text-xs sm:text-sm text-white/80 font-light leading-relaxed">
            Manage your personal profile, gold investment schemes, and verified customer credentials.
          </p>

          <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-[#eddca6] to-transparent mx-auto pt-2"></div>
        </div>
      </section>

      {/* Main Profile Dashboard Container */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#fafaf7] relative z-10">
        <div className="max-w-5xl mx-auto space-y-10">
          
          {/* Notification Toast Alert */}
          {saveSuccess && (
            <div className="bg-emerald-900/90 border border-emerald-400/50 text-white p-4 rounded-2xl shadow-xl flex items-center gap-3 animate-fade-in font-sans">
              <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Check className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider">Profile Updated Successfully</h4>
                <p className="text-[11px] text-emerald-200 font-light">Your investor details have been saved in our secure registry.</p>
              </div>
            </div>
          )}

          {/* Header Profile Cover Card */}
          <div className="bg-white rounded-3xl border border-gold-300 shadow-2xl overflow-hidden">
            {/* Top Cover Banner */}
            <div className="relative h-44 bg-gradient-to-r from-[#072a19] via-[#041a0f] to-[#020906] p-6 text-white border-b border-[#eddca6]/20">
              
              {/* Background Emblem Watermark */}
              <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-15 pointer-events-none">
                <img src={logo} alt="Watermark Logo" className="w-48 h-48 object-contain brightness-0 invert" />
              </div>
            </div>
            
            {/* Profile Action & Avatar Bar */}
            <div className="px-6 sm:px-8 pb-8 pt-4 flex flex-col sm:flex-row sm:items-end justify-between gap-6 relative z-10">
              <div className="flex flex-col sm:flex-row sm:items-end gap-5">
                
                {/* Avatar Frame */}
                <div className="-mt-16 sm:-mt-20 flex-shrink-0 relative z-20">
                  <div className="w-28 h-28 sm:w-32 sm:h-32 bg-[#0b3a24] rounded-full border-4 border-white shadow-2xl flex items-center justify-center overflow-hidden text-[#eddca6] relative ring-2 ring-[#eddca6]/40">
                    {user.photoURL ? (
                      <img 
                        src={user.photoURL} 
                        alt="Profile Avatar" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-14 h-14 text-[#eddca6]" />
                    )}
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-2xl sm:text-3xl font-serif font-black text-[#0b3a24]">
                      {formData.displayName || 'Valued Investor'}
                    </h2>
                    <span className="bg-[#0b3a24] text-[#eddca6] text-[9px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider font-sans">
                      VIP Member
                    </span>
                  </div>
                  <p className="text-xs text-[#0b3a24]/75 font-light">
                    Registered Mobile: <span className="font-semibold font-sans text-[#0b3a24]">{user.phoneNumber}</span>
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                {editing ? (
                  <>
                    <button
                      onClick={() => setEditing(false)}
                      className="flex items-center gap-2 px-5 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors text-xs font-bold uppercase tracking-wider font-sans"
                    >
                      <X className="w-4 h-4" />
                      <span>Cancel</span>
                    </button>
                    <button
                      onClick={handleSave}
                      className="gold-shine-btn flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#eddca6] via-[#fff3d1] to-[#eddca6] text-[#041a0f] rounded-xl shadow-lg hover:scale-105 transition-all text-xs font-extrabold uppercase tracking-widest font-sans"
                    >
                      <Save className="w-4 h-4 text-[#041a0f]" />
                      <span>Save Changes</span>
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setEditing(true)}
                    className="gold-shine-btn flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#0b3a24] via-[#0e482d] to-[#0b3a24] text-white rounded-xl shadow-lg hover:scale-105 transition-all text-xs font-bold uppercase tracking-widest font-sans"
                  >
                    <Edit3 className="w-4 h-4 text-[#eddca6]" />
                    <span>Edit Profile</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Details & Actions Split Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            
            {/* Left Column: Personal Information (7 cols) */}
            <div className="lg:col-span-7 space-y-6">
              <div className="bg-white rounded-3xl p-8 border border-gold-300 shadow-xl space-y-6">
                <div className="flex items-center justify-between border-b border-gold-200 pb-4">
                  <h3 className="text-xl font-serif font-bold text-[#0b3a24]">Personal Information</h3>
                  <span className="text-[9px] font-bold uppercase tracking-widest bg-[#0b3a24]/10 border border-[#0b3a24]/20 text-[#0b3a24] px-3 py-1 rounded-md font-sans">
                    Investor Record
                  </span>
                </div>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    
                    {/* Full Name */}
                    <div className="space-y-2">
                      <label className="block text-[10px] font-bold text-[#0b3a24]/70 uppercase tracking-widest">
                        Full Name
                      </label>
                      {editing ? (
                        <input
                          type="text"
                          name="displayName"
                          value={formData.displayName}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3.5 bg-[#fafaf7] border-b-2 border-gold-300 rounded-xl focus:outline-none focus:border-[#0b3a24] focus:bg-white text-xs font-bold text-[#0b3a24] transition-all font-sans"
                          placeholder="Enter your full name"
                        />
                      ) : (
                        <div className="flex items-center gap-3 p-4 bg-[#fafaf7] border border-gold-200 rounded-2xl text-xs font-bold text-[#0b3a24]">
                          <User className="w-4 h-4 text-[#0b3a24]" />
                          <span>{formData.displayName || 'Not provided'}</span>
                        </div>
                      )}
                    </div>

                    {/* Mobile Number */}
                    <div className="space-y-2">
                      <label className="block text-[10px] font-bold text-[#0b3a24]/70 uppercase tracking-widest">
                        Mobile Number
                      </label>
                      <div className="flex items-center justify-between p-4 bg-[#fafaf7] border border-gold-200 rounded-2xl text-xs font-bold text-[#0b3a24]">
                        <div className="flex items-center gap-3">
                          <Phone className="w-4 h-4 text-[#0b3a24]" />
                          <span>{user.phoneNumber || 'Not provided'}</span>
                        </div>
                        <span className="text-[9px] bg-emerald-50 border border-emerald-200 text-emerald-800 font-bold px-2 py-0.5 rounded font-sans">Verified</span>
                      </div>
                    </div>

                  </div>

                  {/* Email Address */}
                  <div className="space-y-2">
                    <label className="block text-[10px] font-bold text-[#0b3a24]/70 uppercase tracking-widest">
                      Email Address
                    </label>
                    {editing ? (
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3.5 bg-[#fafaf7] border-b-2 border-gold-300 rounded-xl focus:outline-none focus:border-[#0b3a24] focus:bg-white text-xs font-bold text-[#0b3a24] transition-all font-sans"
                        placeholder="Enter your email address"
                      />
                    ) : (
                      <div className="flex items-center gap-3 p-4 bg-[#fafaf7] border border-gold-200 rounded-2xl text-xs font-bold text-[#0b3a24]">
                        <Mail className="w-4 h-4 text-[#0b3a24]" />
                        <span>{formData.email || 'Not provided'}</span>
                      </div>
                    )}
                  </div>

                </div>
              </div>

              {/* Security & Protection Banner */}
              <div className="p-6 bg-gradient-to-r from-[#072a19] via-[#041a0f] to-[#020906] text-white rounded-3xl border border-[#eddca6]/30 shadow-xl flex items-center gap-4">
                <div className="w-12 h-12 bg-[#0b3a24] rounded-2xl flex items-center justify-center text-[#eddca6] flex-shrink-0">
                  <Lock className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-[#eddca6] uppercase tracking-wider font-sans">Secured Investor Account</h4>
                  <p className="text-[11px] text-white/70 font-light leading-relaxed">
                    Protected with end-to-end Firebase Phone OTP authentication and 256-bit encryption.
                  </p>
                </div>
              </div>

            </div>

            {/* Right Column: Account Summary & Navigation Shortcuts (5 cols) */}
            <div className="lg:col-span-5 space-y-6">
              
              {/* Membership Status Card */}
              <div className="bg-white rounded-3xl p-8 border border-gold-300 shadow-xl space-y-5">
                <div className="flex items-center justify-between border-b border-gold-200 pb-3">
                  <h3 className="text-lg font-serif font-bold text-[#0b3a24]">Account Verification</h3>
                  <Award className="w-5 h-5 text-[#c5a85a]" />
                </div>
                
                <div className="space-y-4 text-xs font-semibold">
                  <div className="flex justify-between items-center pb-3 border-b border-gold-100">
                    <span className="text-[#0b3a24]/60 uppercase tracking-wider text-[10px] font-sans">Registration Date</span>
                    <span className="text-[#0b3a24] font-bold font-sans">
                      {user.metadata?.creationTime ? 
                        new Date(user.metadata.creationTime).toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric' }) : 
                        'Active'
                      }
                    </span>
                  </div>

                  <div className="flex justify-between items-center pb-3 border-b border-gold-100">
                    <span className="text-[#0b3a24]/60 uppercase tracking-wider text-[10px] font-sans">Gold Scheme Tier</span>
                    <span className="inline-flex items-center px-3 py-1 rounded-md border border-emerald-300 bg-emerald-50 text-[9px] font-bold text-emerald-800 uppercase font-sans">
                      Active Investor
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-[#0b3a24]/60 uppercase tracking-wider text-[10px] font-sans">Purity Guarantee</span>
                    <span className="text-[#0b3a24] font-bold font-sans text-right">
                      BIS 916 Hallmarked
                    </span>
                  </div>
                </div>
              </div>

              {/* Navigation Shortcuts */}
              <div className="bg-gradient-to-br from-[#072a19] via-[#041a0f] to-[#020906] rounded-3xl p-6 text-white border border-[#eddca6]/30 shadow-xl space-y-3">
                <h4 className="text-xs font-bold text-[#eddca6] uppercase tracking-widest font-sans">Investor Shortcuts</h4>
                
                <button
                  onClick={() => navigate('/passbook')}
                  className="w-full flex items-center justify-between p-3.5 rounded-xl bg-white/10 hover:bg-[#eddca6] text-white hover:text-[#041a0f] transition-all text-xs font-bold uppercase tracking-wider font-sans group shadow-sm"
                >
                  <span className="flex items-center gap-2.5">
                    <BookOpen className="w-4 h-4 text-[#eddca6] group-hover:text-[#041a0f]" />
                    <span>View Passbook Ledger</span>
                  </span>
                  <ChevronRight className="w-4 h-4 text-[#eddca6] group-hover:text-[#041a0f] group-hover:translate-x-1 transition-transform" />
                </button>

                <button
                  onClick={() => navigate('/schemes')}
                  className="w-full flex items-center justify-between p-3.5 rounded-xl bg-white/10 hover:bg-[#eddca6] text-white hover:text-[#041a0f] transition-all text-xs font-bold uppercase tracking-wider font-sans group shadow-sm"
                >
                  <span className="flex items-center gap-2.5">
                    <Award className="w-4 h-4 text-[#eddca6] group-hover:text-[#041a0f]" />
                    <span>Explore Savings Schemes</span>
                  </span>
                  <ChevronRight className="w-4 h-4 text-[#eddca6] group-hover:text-[#041a0f] group-hover:translate-x-1 transition-transform" />
                </button>
              </div>

              {/* Sign Out Button */}
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 p-4 bg-red-50 hover:bg-red-100 text-red-700 rounded-2xl border border-red-200 transition-all text-xs font-bold uppercase tracking-widest shadow-sm font-sans"
              >
                <LogOut className="w-4 h-4" />
                <span>Sign Out Account</span>
              </button>

            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
